// `ScreenplayScript` and `ScriptLayout` (SuiteCompile/Screenplay.swift): a compiled screenplay's Fountain as printed
// elements and its title page, laid out in pages the way screenplay software does, so a page stays a minute: every
// element at its column, wrapped to its width; scene headings never alone at the foot of a page; action and speeches
// broken across pages only at a sentence end, with two lines at least either side, and a speech's halves marked
// (MORE) below and with the name and (CONT'D) above; anything that can't break well moves whole to the next page.
import {
  fountainParagraphs, fountainParagraphText, fountainTitlePage, serialize,
  type ClassifiedElement, type FountainParagraph, type FountainRun,
} from '@writing-suite/core';
import { bylineName, contactName, isPenName } from '../manuscript.ts';
import type { CompiledManuscript } from '../types.ts';

export type Run = FountainRun;
export type Paragraph = FountainParagraph;
export type Element = ClassifiedElement;

export const run = (text: string, style: Partial<Omit<Run, 'text'>> = {}): Run =>
  ({ text, italic: style.italic ?? false, bold: style.bold ?? false, underline: style.underline ?? false });

// MARK: Characters (Swift counts grapheme clusters)

const segmenter = new Intl.Segmenter();
/** A string's Characters. */
export function chars(s: string): string[] {
  // eslint-disable-next-line no-control-regex
  if (/^[\x00-\x7f]*$/.test(s) && !s.includes('\r\n')) return s.split('');
  return Array.from(segmenter.segment(s), (x) => x.segment);
}
export const count = (s: string) => chars(s).length;

/** `trimmingCharacters(in: .whitespaces)`. */
const trimSpaces = (s: string) => s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');

// MARK: ScreenplayScript

export interface TitleField { key: string; value: string }

export interface ScreenplayScript {
  title: string;
  /** Fountain title-page keys (`title`, `credit`, `author`, `contact`, `draft date`…) in order. */
  titlePage: TitleField[];
  paragraphs: Paragraph[];
}

const cloneParagraph = (p: Paragraph): Paragraph => ({ ...p, lines: p.lines.map((l) => l.map((r) => ({ ...r }))) });

/** `ScreenplayScript.init(_ m:)`. */
export function screenplayScript(m: CompiledManuscript): ScreenplayScript {
  const settings = m.preset.render.screenplay;
  const sources = m.sections.filter((s) => s.matter === null).flatMap((s) => {
    if (s.source !== null) return [s.source];
    return s.blocks.length ? [serialize(s.blocks)] : [];
  });
  // A title page written in Fountain at the top of the script wins; otherwise one is made from the project and the
  // author (left out when blind).
  const firstWritten = sources.find((s) => !/^\p{White_Space}*$/u.test(s));
  const written = firstWritten !== undefined ? fountainTitlePage(firstWritten) : [];
  let titlePage: TitleField[];
  if (written.length) {
    titlePage = written.map((f) => ({ key: f.key, value: f.value }));
  } else {
    titlePage = [{ key: 'title', value: m.title }];
    if (m.author && m.preset.render.titlePage.byline) {
      titlePage.push({ key: 'credit', value: 'Written by' }, { key: 'author', value: bylineName(m.author) });
      if (m.preset.render.titlePage.contact && m.author.contact.length) {
        // Under a pen name, the contact block says who to write to.
        const lines = [...(isPenName(m.author) ? [contactName(m.author)] : []), ...m.author.contact];
        titlePage.push({ key: 'contact', value: lines.join('\n') });
      }
    }
  }
  const paragraphs = sources.flatMap((s) => fountainParagraphs(s)).map(cloneParagraph);
  if (settings?.continueds ?? true) addContinueds(paragraphs);
  if (settings?.sceneNumbers === true) {
    let n = 0;
    for (const p of paragraphs) {
      if (p.element !== 'sceneHeading') continue;
      n += 1;
      if (p.sceneNumber === undefined) p.sceneNumber = String(n);
    }
  } else {
    for (const p of paragraphs) delete p.sceneNumber;
  }
  return { title: m.title, titlePage, paragraphs };
}

/** `ScreenplayScript.speaker`: a cue's name without its extension: "MARA (V.O.)" → "MARA". (Swift's `split`, which
 *  skips empty pieces: the first non-empty piece before a "(", else the rest.) */
export function speaker(cue: string): string {
  let start = 0, name: string | null = null;
  for (let i = 0; i < cue.length; i++) {
    if (cue[i] !== '(') continue;
    if (i > start) { name = cue.slice(start, i); break; }
    start = i + 1;
  }
  if (name === null) name = start < cue.length ? cue.slice(start) : cue;
  return trimSpaces(name.replaceAll('^', ''));
}

/** `addContinueds`: "(CONT'D)" on a cue whose character spoke last in the same scene, with only action between. */
export function addContinueds(paragraphs: Paragraph[]): void {
  let last: string | null = null;
  for (const p of paragraphs) {
    switch (p.element) {
      case 'sceneHeading': case 'transition': case 'pageBreak': last = null; break;
      case 'character': {
        const cue = fountainParagraphText(p);
        const name = speaker(cue);
        const upper = cue.toUpperCase();
        if (name === last && !p.isDual && !upper.includes("CONT'D") && !upper.includes('CONT’D') && p.lines.length) {
          const line = p.lines[0];
          const end = line[line.length - 1];
          if (end && !end.italic && !end.bold && !end.underline) end.text += " (CONT'D)";
          else line.push(run(" (CONT'D)"));
        }
        last = p.isDual ? null : name;
        break;
      }
      default: break;
    }
  }
}

// MARK: Pagination

/** One printed line of a script page, on a grid of Courier columns and 12 pt lines. */
export interface ScriptLine {
  row: number;
  /** Columns (tenths of an inch) from the left margin. */
  column: number;
  runs: Run[];
  sceneNumber?: string;
  isSceneHeading: boolean;
}

export interface ScriptPage { lines: ScriptLine[] }

export const LEFT_MARGIN = 1.5; // inches
export const TOP_MARGIN = 1.0;
export const BOTTOM_MARGIN = 1.0;
/** Columns of action. */
export const WIDTH = 60;

interface SpeechColumns { cue: number; cueWidth: number; paren: number; parenWidth: number; dialogue: number; dialogueWidth: number }
const SINGLE: SpeechColumns = { cue: 22, cueWidth: 38, paren: 16, parenWidth: 25, dialogue: 10, dialogueWidth: 35 };
const DUAL_LEFT: SpeechColumns = { cue: 8, cueWidth: 20, paren: 3, parenWidth: 22, dialogue: 0, dialogueWidth: 28 };
const DUAL_RIGHT: SpeechColumns = { cue: 39, cueWidth: 21, paren: 34, parenWidth: 22, dialogue: 31, dialogueWidth: 29 };

interface Piece { column: number; runs: Run[]; sceneNumber?: string; isSceneHeading: boolean }
const piece = (column: number, runs: Run[]): Piece => ({ column, runs, isSceneHeading: false });

type SegmentKind = 'action' | 'dialogue' | 'parenthetical' | 'lyric';
/** One line of an element as written, before wrapping: what breaks across pages. */
interface Segment { runs: Run[]; kind: SegmentKind }

function pieces(s: Segment, c: SpeechColumns): Piece[] {
  switch (s.kind) {
    case 'action': return wrap(s.runs, WIDTH).map((r) => piece(0, r));
    case 'dialogue': return wrap(s.runs, c.dialogueWidth).map((r) => piece(c.dialogue, r));
    case 'lyric': return wrap(s.runs.map((r) => ({ ...r, italic: true })), c.dialogueWidth).map((r) => piece(c.dialogue, r));
    // Wrapped lines sit inside the opening parenthesis.
    case 'parenthetical': return wrap(s.runs, c.parenWidth, 1).map((r, i) => piece(c.paren + (i > 0 ? 1 : 0), r));
  }
}

interface Speech { name: string; cue: Piece[]; segments: Segment[]; columns: SpeechColumns }
const body = (s: Speech) => s.segments.flatMap((g) => pieces(g, s.columns));
const height = (s: Speech) => s.cue.length + body(s).length;

type Unit =
  /** Headings, transitions, centred text: never broken. */
  | { type: 'block'; element: Element; lines: Piece[] }
  | { type: 'action'; segments: Segment[] }
  | { type: 'speech'; speech: Speech }
  | { type: 'dual'; left: Speech; right: Speech }
  | { type: 'pageBreak' };

/** The least of a unit that may start a page's last lines (for keeping a heading with it). */
function head(u: Unit): number {
  switch (u.type) {
    case 'block': return Math.min(2, u.lines.length);
    case 'action': return Math.min(2, u.segments.reduce((n, s) => n + pieces(s, SINGLE).length, 0));
    case 'speech': { const b = body(u.speech).length; return u.speech.cue.length + Math.min(2, b) + (b > 2 ? 1 : 0); }
    case 'dual': return Math.max(height(u.left), height(u.right));
    case 'pageBreak': return 0;
  }
}

export interface ScriptLayout {
  pages: ScriptPage[];
  linesPerPage: number;
}

/** `ScriptLayout.init`: `pageHeight` in inches; `headingSpacing` = blank lines before a scene heading. */
export function scriptLayout(script: ScreenplayScript, pageHeight = 11, headingSpacing = 1): ScriptLayout {
  const linesPerPage = Math.max(20, Math.floor(((pageHeight - TOP_MARGIN - BOTTOM_MARGIN) * 72) / 12));
  const all = units(script.paragraphs);
  const pages: ScriptLine[][] = [[]];
  let row = 0;
  const capacity = linesPerPage;

  const space = (u: Unit) => (u.type === 'block' && u.element === 'sceneHeading' ? Math.max(1, headingSpacing) : 1);
  const room = (gap: number) => capacity - row - (row > 0 ? gap : 0);
  const newPage = () => {
    if (row > 0) pages.push([]);
    row = 0;
  };
  const place = (list: Piece[], gap: number) => {
    if (row > 0) row += gap;
    for (const p of list) {
      if (row >= capacity) newPage();
      pages[pages.length - 1].push({ row, column: p.column, runs: p.runs, ...(p.sceneNumber !== undefined ? { sceneNumber: p.sceneNumber } : {}), isSceneHeading: p.isSceneHeading });
      row += 1;
    }
  };
  const placeAction = (segments: Segment[], gap: number): void => {
    const lines = segments.flatMap((s) => pieces(s, SINGLE));
    if (lines.length <= room(gap)) return place(lines, gap);
    const cut = split(segments, SINGLE, room(gap), 2, 2);
    if (cut) {
      place(cut.head.flatMap((s) => pieces(s, SINGLE)), gap);
      newPage();
      return placeAction(cut.tail, 0);
    }
    if (row > 0) { newPage(); return placeAction(segments, 0); }
    place(lines, 0); // a page of action with nowhere to break: break where it must
  };
  const placeSpeech = (s: Speech, gap: number): void => {
    if (height(s) <= room(gap)) return place([...s.cue, ...body(s)], gap);
    const more = piece(SINGLE.cue, [run('(MORE)')]);
    const contd = s.name === '' ? [] : [piece(SINGLE.cue, [run(`${s.name} (CONT'D)`)])];
    const cut = split(s.segments, s.columns, room(gap) - s.cue.length - 1, 2, 2);
    if (cut) {
      place([...s.cue, ...body({ name: s.name, cue: [], segments: cut.head, columns: s.columns }), more], gap);
      newPage();
      return placeSpeech({ name: s.name, cue: contd, segments: cut.tail, columns: s.columns }, 0);
    }
    if (row > 0) { newPage(); return placeSpeech(s, 0); }
    place([...s.cue, ...body(s)], 0); // a page-long speech with no sentence to break at
  };

  all.forEach((unit, k) => {
    const gap = space(unit);
    switch (unit.type) {
      case 'pageBreak':
        newPage();
        break;
      case 'block': {
        let need = unit.lines.length;
        // A scene heading keeps the start of what follows it on its page.
        if (unit.element === 'sceneHeading' && k + 1 < all.length) need += space(all[k + 1]) + head(all[k + 1]);
        if (need > room(gap)) newPage();
        place(unit.lines, gap);
        break;
      }
      case 'action':
        placeAction(unit.segments, gap);
        break;
      case 'speech':
        placeSpeech(unit.speech, gap);
        break;
      case 'dual': {
        const tallest = Math.max(height(unit.left), height(unit.right));
        // Side-by-side speeches can't break across a page; taller than a whole page, they're set one after the
        // other and break as single speeches do.
        if (tallest > capacity) {
          [unit.left, unit.right].forEach((s, i) => {
            const cue = wrap(s.cue.flatMap((p) => p.runs), SINGLE.cueWidth).map((r) => piece(SINGLE.cue, r));
            placeSpeech({ name: s.name, cue, segments: s.segments, columns: SINGLE }, i === 0 ? gap : 1);
          });
          break;
        }
        if (tallest > room(gap)) newPage();
        if (row > 0) row += gap;
        for (const column of [unit.left, unit.right]) {
          [...column.cue, ...body(column)].forEach((p, i) => {
            pages[pages.length - 1].push({ row: row + i, column: p.column, runs: p.runs, isSceneHeading: false });
          });
        }
        row += tallest;
        break;
      }
    }
  });
  if (pages.length > 1 && pages[pages.length - 1].length === 0) pages.pop();
  return { pages: pages.map((lines) => ({ lines })), linesPerPage };
}

/** `ScriptLayout.split`: the latest place `segments` can break with between `before` and `available` lines first and
 *  at least `after` left: between two lines as written (never after a parenthetical), or at a sentence end within
 *  one, which then wraps as two. */
function split(segments: Segment[], columns: SpeechColumns, available: number, before: number, after: number): { head: Segment[]; tail: Segment[] } | null {
  if (available < before) return null;
  const heights = segments.map((s) => pieces(s, columns).length);
  const total = heights.reduce((a, b) => a + b, 0);
  let best: { head: Segment[]; tail: Segment[] } | null = null;
  let used = 0;
  for (let k = 0; k < segments.length; k++) {
    const segment = segments[k];
    if (k > 0 && segments[k - 1].kind !== 'parenthetical' && used >= before && used <= available && total - used >= after) {
      best = { head: segments.slice(0, k), tail: segments.slice(k) };
    }
    if (segment.kind !== 'parenthetical') {
      const rest = heights.slice(k + 1).reduce((a, b) => a + b, 0);
      for (const offset of sentenceEnds(segment.runs)) {
        const [h, t] = cut(segment.runs, offset);
        const headSegment: Segment = { runs: h, kind: segment.kind }, tailSegment: Segment = { runs: t, kind: segment.kind };
        const lines = used + pieces(headSegment, columns).length;
        if (lines > available) break;
        if (lines >= before && pieces(tailSegment, columns).length + rest >= after) {
          best = { head: [...segments.slice(0, k), headSegment], tail: [tailSegment, ...segments.slice(k + 1)] };
        }
      }
    }
    used += heights[k];
    if (used > available) break;
  }
  return best;
}

/** Character offsets just after each sentence inside `runs` ("…end." then a space), in order. */
export function sentenceEnds(runs: Run[]): number[] {
  const c = chars(runs.map((r) => r.text).join(''));
  const ends: number[] = [];
  let i = 0;
  while (i < c.length) {
    if ('.!?…'.includes(c[i]) && c[i].length > 0) {
      let j = i + 1;
      while (j < c.length && '"\'”’)'.includes(c[j])) j += 1;
      if (j < c.length && c[j] === ' ' && c.slice(j).some((x) => x !== ' ')) ends.push(j);
      i = j;
    } else {
      i += 1;
    }
  }
  return ends;
}

/** Splits styled text at a Character offset; the second part loses its leading spaces. */
export function cut(runs: Run[], offset: number): [Run[], Run[]] {
  const head: Run[] = [], tail: Run[] = [];
  let seen = 0;
  for (const r of runs) {
    const c = chars(r.text);
    const n = c.length;
    if (seen + n <= offset) head.push(r);
    else if (seen >= offset) tail.push(r);
    else {
      head.push({ ...r, text: c.slice(0, offset - seen).join('') });
      tail.push({ ...r, text: c.slice(offset - seen).join('') });
    }
    seen += n;
  }
  while (tail.length && tail[0].text.startsWith(' ')) {
    tail[0] = { ...tail[0], text: tail[0].text.replace(/^ +/, '') };
    if (tail[0].text === '') tail.shift();
  }
  return [head, tail];
}

/** Printed elements grouped into what pagination moves: blocks, action, speeches, dual speeches. */
function units(paragraphs: Paragraph[]): Unit[] {
  const out: Unit[] = [];
  const speechElements = new Set<Element>(['dialogue', 'parenthetical', 'lyric']);
  // The dialogue and parentheticals from `start`, as written.
  const segmentsFrom = (start: number): [Segment[], number] => {
    const segments: Segment[] = [];
    let j = start;
    while (j < paragraphs.length && speechElements.has(paragraphs[j].element)) {
      const kind: SegmentKind = paragraphs[j].element === 'parenthetical' ? 'parenthetical' : paragraphs[j].element === 'lyric' ? 'lyric' : 'dialogue';
      for (const line of paragraphs[j].lines) segments.push({ runs: line, kind });
      j += 1;
    }
    return [segments, j];
  };
  const speechAt = (start: number, c: SpeechColumns): [Speech, number] => {
    const cue = paragraphs[start];
    const [segments, next] = segmentsFrom(start + 1);
    const cueRuns = (cue.lines[0] ?? []).map((r) => ({ ...r, text: r.text.replaceAll('^', '') }));
    const cuePieces = wrap(cueRuns, c.cueWidth).map((r) => piece(c.cue, r));
    return [{ name: speaker(fountainParagraphText(cue)), cue: cuePieces, segments, columns: c }, next];
  };
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    switch (p.element) {
      case 'pageBreak':
        out.push({ type: 'pageBreak' });
        i += 1;
        break;
      case 'character': {
        // Dual dialogue: this speech and the next, when the next cue is marked ^.
        const [, next] = segmentsFrom(i + 1);
        if (next < paragraphs.length && paragraphs[next].element === 'character' && paragraphs[next].isDual) {
          const [left, afterLeft] = speechAt(i, DUAL_LEFT);
          const [right, afterRight] = speechAt(afterLeft, DUAL_RIGHT);
          out.push({ type: 'dual', left, right });
          i = afterRight;
        } else {
          const [speech, after] = speechAt(i, SINGLE);
          out.push({ type: 'speech', speech });
          i = after;
        }
        break;
      }
      case 'dialogue': case 'parenthetical': {
        // Dialogue with no cue (a character cut above it): still set as dialogue.
        const [segments, after] = segmentsFrom(i);
        out.push({ type: 'speech', speech: { name: '', cue: [], segments, columns: SINGLE } });
        i = after;
        break;
      }
      case 'action':
        out.push({ type: 'action', segments: p.lines.map((runs) => ({ runs, kind: 'action' as const })) });
        i += 1;
        break;
      default: {
        const list: Piece[] = [];
        for (const line of p.lines) {
          switch (p.element) {
            case 'transition':
              for (const w of wrap(line, WIDTH)) list.push(piece(WIDTH - length(w), w));
              break;
            case 'centered':
              for (const w of wrap(line, WIDTH)) list.push(piece(Math.trunc((WIDTH - length(w)) / 2), w));
              break;
            case 'lyric':
              list.push(...pieces({ runs: line, kind: 'lyric' }, SINGLE));
              break;
            default:
              for (const w of wrap(line, WIDTH)) list.push(piece(0, w));
          }
        }
        if (p.element === 'sceneHeading' && list.length) {
          if (p.sceneNumber !== undefined) list[0].sceneNumber = p.sceneNumber;
          for (const x of list) x.isSceneHeading = true;
        }
        out.push({ type: 'block', element: p.element, lines: list });
        i += 1;
      }
    }
  }
  return out;
}

/** Columns a line takes: its printed Characters. */
export const length = (runs: Run[]) => runs.reduce((n, r) => n + count(r.text), 0);

const styleOf = (r: Run) => (r.italic ? 1 : 0) | (r.bold ? 2 : 0) | (r.underline ? 4 : 0);

/** `ScriptLayout.wrap`: word-wraps styled text to `width` columns (later lines `hang` narrower); leading spaces on the
 *  first line are the writer's indentation and stay; a word longer than a line is broken. */
export function wrap(runs: Run[], width: number, hang = 0): Run[][] {
  const glyphs: { c: string; style: number }[] = runs.flatMap((r) => chars(r.text).map((c) => ({ c, style: styleOf(r) })));
  const lines: { c: string; style: number }[][] = [];
  let current: { c: string; style: number }[] = [];
  let limit = Math.max(1, width);
  let i = 0;
  while (i < glyphs.length) {
    let j = i;
    while (j < glyphs.length && glyphs[j].c === ' ') j += 1;
    let k = j;
    while (k < glyphs.length && glyphs[k].c !== ' ') k += 1;
    // Wrapped lines don't start with the space that broke them.
    const lead = current.length === 0 && lines.length > 0 ? [] : glyphs.slice(i, j);
    const word = glyphs.slice(j, k);
    if (current.length + lead.length + word.length <= limit) {
      current = [...current, ...lead, ...word];
      i = k;
    } else if (current.length === 0 || current.every((g) => g.c === ' ')) {
      // Too long for any line: break the word.
      const roomLeft = Math.max(1, limit - current.length);
      current = [...current, ...word.slice(0, roomLeft)];
      lines.push(current);
      current = [];
      limit = Math.max(1, width - hang);
      i = j + Math.min(roomLeft, word.length);
    } else {
      lines.push(current);
      current = [];
      limit = Math.max(1, width - hang);
      i = j;
    }
  }
  if (current.length || lines.length === 0) lines.push(current);
  return lines.map((line) => {
    const out: Run[] = [];
    for (const g of line) {
      const last = out[out.length - 1];
      if (last && styleOf(last) === g.style) last.text += g.c;
      else out.push(run(g.c, { italic: (g.style & 1) !== 0, bold: (g.style & 2) !== 0, underline: (g.style & 4) !== 0 }));
    }
    return out;
  });
}

/** `ScreenplayScript.pageCount(fountain:)`: the pages the open text would print on (Letter, no title page). */
export function fountainPageCount(texts: string[]): number {
  const paragraphs = texts.flatMap((t) => fountainParagraphs(t)).map(cloneParagraph);
  addContinueds(paragraphs);
  return paragraphs.length === 0 ? 0 : scriptLayout({ title: '', titlePage: [], paragraphs }).pages.length;
}
